import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MomentPipe} from '../../../pipes/moment.pipe';
import {MoneyPipe} from '../../../pipes/money.pipe';
import {CurrencyPipe, NgIf} from '@angular/common';
import {PaymentFormComponent} from '../../../payment-form/payment-form.component';
import {Router, RouterLink} from '@angular/router';
import {firstValueFrom, Subject, takeUntil} from 'rxjs';
import {Room} from '../../../../models/room';
import {Product} from '../../../../models/product';
import {Availability} from '../../../../models/schedule';
import {BookingPrice} from '../../../../models/price';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../toast/toast.service';
import {OrderService} from '../../../../services/order.service';
import moment from 'moment/moment';
import {User} from '../../../../models/user';

@Component({
  selector: 'app-booking-confirm-modal',
  imports: [
    FormsModule,
    MomentPipe,
    MoneyPipe,
    NgIf,
    PaymentFormComponent,
    RouterLink
  ],
  providers: [CurrencyPipe],
  templateUrl: './booking-confirm-modal.component.html',
  styleUrl: './booking-confirm-modal.component.scss'
})
export class BookingConfirmModalComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  @Input() currentUser!: User
  @Input() regionSlug!: string
  @Input() room!: Room
  @Input() product!: Product
  @Input() promotion: Product | null = null
  @Input() availability!: Availability
  @Input() bookingPrice!: BookingPrice

  // Order
  submitted = false;

  // Wallet
  @ViewChild(PaymentFormComponent) paymentForm!: PaymentFormComponent;
  isPaymentFormLoaded = false
  isPaymentFormValid = false

  constructor(
    private modal: NgbActiveModal,
    private router: Router,
    private toast: ToastService,
    private orderService: OrderService) {
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  async submit() {
    if (!this.product) return this.toast.failure("Please select a product")
    if (!this.isPaymentFormValid) return this.toast.failure("Please provide a valid payment")

    this.submitted = true;
    try {

      // Payment methods (don't show for team accounts)
      if (!this.currentUser.company) {
        await this.paymentForm.onCreateOrRetrieveWallet();
      }

      // Create order
      await this.createOrderPromise();

      // Success message
      this.toast.success('Success! Check your email for the confirmation.');
      this.router.navigate(['/activity']).finally();

      // Close modal
      this.close()
    } catch (error: any) {

      // Display the error message
      let message = 'There was a problem. Please refresh and try again or contact support for assistance.';
      if (error && error.error && error.error.message) {
        message = error.error.message.toString();
      } else if (error && error.error && error.error.error && error.error.error.message) {
        message = error.error.error.message.toString();
      } else if (error && error.data && error.data.error && error.data.error.message) {
        message = error.data.error.message.toString();
      } else if (error) {
        message = error.toString();
      }
      this.toast.failure(message);

    } finally {
      this.submitted = false;
    }
  }

  async createOrderPromise() {
    if (!this.product) return Promise.reject("Product is required")
    if (!this.room) return Promise.reject("Room is required")
    if (!this.availability) return Promise.reject("Availability is required")
    if (!this.bookingPrice) return Promise.reject("Booking price is required")

    const productIds: string[] = this.product ? [this.product._id!] : [];
    const referralIds: string[] = []; // Disabled since there's not going to be any referrals occurring
    const promotionIds: string[] = []; // Disabled since there's not going to be any promotion occurring
    const placeId = this.room.place._id;
    const roomId = this.room._id;
    const localBegin = moment(this.availability.localBegin, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
    const localEnd = moment(this.availability.localEnd, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
    const totalPrice = this.bookingPrice.totalBalance;

    return firstValueFrom(this.orderService.createBookingOrder(productIds, referralIds, promotionIds, placeId, roomId, localBegin, localEnd, totalPrice).pipe(takeUntil(this.onDestroy$)))
  }

  onPaymentFormLoaded(loaded: boolean) {
    this.isPaymentFormLoaded = loaded
  }

  onPaymentFormValid(valid: boolean) {
    this.isPaymentFormValid = valid
  }

  close() {
    this.modal.close()
  }

}
